import clsx from 'clsx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatMoney } from '../lib/format';
import {
  REDEMPTION_TYPES,
  calendarDate,
  errorKey,
  isPenaltyRedemption,
  newIdempotencyKey,
  pointsText,
  reasonKey,
  timesBundles,
  type EligiblePenalty,
  type HeldBack,
  type RedeemResult,
  type RedemptionType,
  type WalletOptions,
} from '../lib/wallet';
import { FormPage } from '../components/FormPage';
import { Detail, Field, Input, useToast } from '../components/ui';
import type { LeaveBalance } from './Leaves';

/**
 * Confirming a redemption, `/wallet/redeem/:type` — a page, as every form in the
 * admin is. It says what it costs, the balance before and after, and exactly what
 * it buys; nothing is spent until Redeem is pressed. The price is the server's:
 * the page sends the version it showed, and a price changed meanwhile comes back
 * as PRICE_CHANGED with the new one, to be confirmed again. Each confirmation
 * carries its own key, so a double click or a retry is charged once.
 */
export function WalletRedeemPage() {
  const { type: raw = '' } = useParams();
  const type = (REDEMPTION_TYPES as string[]).includes(raw) ? (raw as RedemptionType) : null;
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [penaltyId, setPenaltyId] = useState('');
  const [bundles, setBundles] = useState('1');
  const [key, setKey] = useState(newIdempotencyKey);
  const [priceMoved, setPriceMoved] = useState(false);

  const options = useQuery({ queryKey: ['wallet', 'options'], queryFn: () => api.get<WalletOptions>('/wallet/me/options') });
  const option = options.data?.options.find((o) => o.type === type) ?? null;
  const balance = options.data?.balance ?? 0;
  const isPenalty = !!type && isPenaltyRedemption(type);

  const penalties = useQuery({
    queryKey: ['wallet', 'penalties', type],
    queryFn: () => api.get<{ items: EligiblePenalty[]; heldBack: HeldBack }>('/wallet/me/penalties', { type: type! }),
    enabled: isPenalty,
  });
  const leaveYear = option?.year ?? new Date().getFullYear();
  const leave = useQuery({
    queryKey: ['leaves', 'balance', leaveYear],
    queryFn: () => api.get<LeaveBalance[]>('/leaves/balance', { year: leaveYear }),
    enabled: type === 'ADD_ONE_LEAVE_DAY',
  });

  // One penalty to cancel: picked for them. Several: they choose.
  useEffect(() => {
    const items = penalties.data?.items ?? [];
    if (items.length === 1) setPenaltyId(items[0].id);
    else if (!items.some((p) => p.id === penaltyId)) setPenaltyId('');
  }, [penalties.data, penaltyId]);

  const cash = option?.cash ?? null;
  const count = Number(bundles);
  const bundlesValid = Number.isInteger(count) && count >= 1 && (!cash || count <= Math.max(1, cash.maxBundles));
  const cost = option?.points == null ? null : type === 'CASH_REWARD' ? (bundlesValid ? count * option.points : null) : option.points;
  const after = cost == null ? null : balance - cost;
  const annual = leave.data?.find((b) => b.leaveType.code === 'ANNUAL') ?? null;

  const ready =
    !!option &&
    option.available &&
    !!option.priceVersion &&
    cost != null &&
    after != null &&
    after >= 0 &&
    (!isPenalty || !!penaltyId) &&
    (type !== 'CASH_REWARD' || bundlesValid);

  const redeem = useMutation({
    mutationFn: () =>
      api.post<RedeemResult>('/wallet/me/redemptions', {
        type,
        priceVersion: option!.priceVersion,
        idempotencyKey: key,
        ...(isPenalty ? { penaltyId } : {}),
        ...(type === 'CASH_REWARD' ? { bundles: count } : {}),
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['wallet'] });
      void queryClient.invalidateQueries({ queryKey: ['leaves'] });
      void queryClient.invalidateQueries({ queryKey: ['penalties'] });
      toast.success(
        t(
          result.replayed
            ? 'wallet.done.replayed'
            : isPenalty
              ? 'wallet.done.penalty'
              : type === 'ADD_ONE_LEAVE_DAY'
                ? 'wallet.done.leave'
                : 'wallet.done.cash',
        ),
      );
      navigate('/wallet');
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
        // Refused, and nothing was kept under this key: the next confirmation gets a new one.
        setKey(newIdempotencyKey());
        if (e.code === 'PRICE_CHANGED') setPriceMoved(true);
        void queryClient.invalidateQueries({ queryKey: ['wallet'] });
        toast.error(t(errorKey(e.code, e.details as { reason?: string } | null), { currency: cash?.currency ?? '' }));
      } else {
        // No answer, or the server's own error: whether it went through is not known, so the key
        // stays — pressing again returns the first outcome, and is never charged twice.
        toast.error(t('wallet.error.network'));
      }
    },
  });

  const reward = type ? t(`wallet.type.${type}` as TranslationKey) : '';
  const reason = option ? reasonKey(option) : null;

  return (
    <FormPage
      title={type ? reward : t('wallet.title')}
      subtitle={t('wallet.redeemPageHint')}
      backTo="/wallet"
      backLabel={t('wallet.title')}
      submitLabel={cost != null ? t('wallet.confirmSubmit', { points: pointsText(cost, locale, t) }) : t('wallet.redeem')}
      onSubmit={() => redeem.mutate()}
      submitting={redeem.isPending}
      disabled={!ready}
      loading={options.isLoading}
      error={options.error ?? (!type || (options.data && !option) ? new Error(t('wallet.noSuchReward')) : null)}
      onRetry={() => void options.refetch()}
      summary={
        option && (
          <dl className="grid gap-x-8 sm:grid-cols-2">
            <Detail label={t('wallet.cost')}>
              <span className="tabular font-semibold">{cost != null ? pointsText(cost, locale, t) : '—'}</span>
            </Detail>
            <Detail label={t('wallet.youGet')}>
              {isPenalty
                ? t('wallet.get.penalty', { days: option.penaltyDays ?? '' })
                : type === 'ADD_ONE_LEAVE_DAY'
                  ? t('wallet.get.leave', {
                      type: option.leaveType ? localized(option.leaveType) : t('wallet.annualLeave'),
                      year: option.year ?? '',
                    })
                  : cash && bundlesValid
                    ? t('wallet.get.cash', { amount: formatMoney(timesBundles(cash.amountPerBundle, count), cash.currency, locale) })
                    : '—'}
            </Detail>
            <Detail label={t('wallet.balanceNow')}>
              <span className="tabular">{pointsText(balance, locale, t)}</span>
            </Detail>
            <Detail label={t('wallet.balanceAfterRedeem')}>
              <span className={clsx('tabular', after != null && after < 0 && 'text-error-fg dark:text-error-dark')}>
                {after != null ? pointsText(after, locale, t) : '—'}
              </span>
            </Detail>
          </dl>
        )
      }
    >
      {option && (
        <div className="space-y-5">
          {priceMoved && (
            <p className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning-bg px-3 py-2 text-body-sm text-warning-fg dark:bg-warning/14 dark:text-warning-dark">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              {t('wallet.priceChanged')}
            </p>
          )}

          {!option.available && reason && (
            <p className="rounded-md border hairline bg-[var(--surface-sunken)] px-3 py-2 text-body-sm text-[var(--text-secondary)]">
              {t(reason, { currency: cash?.currency ?? '' })}
            </p>
          )}

          {isPenalty && option.available && (
            <fieldset>
              <legend className="mb-1 text-body-sm font-medium text-[var(--text-secondary)]">{t('wallet.pickPenalty')}</legend>
              <p className="mb-3 text-caption text-faint">{t('wallet.pickPenaltyHint')}</p>
              <div className="space-y-2">
                {(penalties.data?.items ?? []).map((p) => {
                  const detail = locale === 'ar' ? p.detailAr ?? p.detail : p.detail;
                  return (
                    <label
                      key={p.id}
                      className={clsx(
                        'flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2.5 transition-colors duration-fast',
                        penaltyId === p.id ? 'border-primary bg-primary/6' : 'hairline hover:border-primary/50',
                      )}
                    >
                      <input
                        type="radio"
                        name="penalty"
                        value={p.id}
                        checked={penaltyId === p.id}
                        onChange={() => setPenaltyId(p.id)}
                        className="h-4 w-4 accent-primary"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-body-sm text-[var(--text-primary)]">
                          {t(`wallet.penaltyKind.${p.kind}` as TranslationKey)}
                          {detail && (
                            <span className="text-muted">
                              {' · '}
                              <bdi>{detail}</bdi>
                            </span>
                          )}
                        </span>
                        <span className="block text-caption text-muted tabular">{calendarDate(p.date, locale)}</span>
                      </span>
                      <span className="shrink-0 text-caption text-error-fg tabular dark:text-error-dark">
                        {t('wallet.penaltyDays', { days: p.days })}
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          )}

          {type === 'CASH_REWARD' && cash && option.available && (
            <Field
              label={t('wallet.bundles')}
              hint={t('wallet.bundlesHint', {
                points: pointsText(cash.pointsPerBundle, locale, t),
                amount: formatMoney(cash.amountPerBundle, cash.currency, locale),
                max: cash.maxBundles,
              })}
              error={bundles !== '' && !bundlesValid ? t('wallet.bundlesInvalid', { max: cash.maxBundles }) : null}
            >
              <Input
                type="number"
                inputMode="numeric"
                min={1}
                max={cash.maxBundles}
                step={1}
                className="max-w-[9rem] tabular"
                value={bundles}
                onChange={(e) => setBundles(e.target.value)}
              />
            </Field>
          )}

          {type === 'CASH_REWARD' && option.available && <p className="text-caption text-muted">{t('wallet.cashNote')}</p>}

          {type === 'ADD_ONE_LEAVE_DAY' && option.available && annual?.remaining != null && (
            <p className="text-body-sm text-[var(--text-secondary)]">
              {t('wallet.leaveBalance', { before: annual.remaining, after: annual.remaining + 1 })}
            </p>
          )}

          {ready && cost != null && (
            <p className="text-body-sm font-medium text-[var(--text-primary)]">
              {t('wallet.confirmQuestion', { points: pointsText(cost, locale, t), reward })}
            </p>
          )}
        </div>
      )}
    </FormPage>
  );
}
